import React from 'react'
import { Alert, Button, Chip, CircularProgress, LinearProgress } from '@mui/material'
import { CreativeImportRowStatus } from '@marketing-milk/interfaces'
import { CreativeImportState } from '../state'

interface Props {
  state: CreativeImportState
  onConfirm: () => void
  onBack: () => void
}

const ROW_STATUS_CHIP: Record<
  CreativeImportRowStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'error' }
> = {
  [CreativeImportRowStatus.NotSelected]: { label: 'Not selected', color: 'default' },
  [CreativeImportRowStatus.Pending]: { label: 'Waiting', color: 'default' },
  [CreativeImportRowStatus.Processing]: { label: 'Importing…', color: 'info' },
  [CreativeImportRowStatus.Done]: { label: 'Done', color: 'success' },
  [CreativeImportRowStatus.Error]: { label: 'Failed', color: 'error' },
}

export const ImportStep = ({ state, onConfirm, onBack }: Props) => {
  const selectedCount = Object.values(state.rows).filter(row => row.selected).length
  const progress = state.progress
  const started = state.confirming || progress !== null

  if (!started) {
    return (
      <div className="flex flex-col gap-4">
        {state.confirmError && <Alert severity="error">{state.confirmError}</Alert>}
        <Alert severity="info">
          This will upload {selectedCount} image{selectedCount === 1 ? '' : 's'} to Facebook and
          create {selectedCount} draft creative{selectedCount === 1 ? '' : 's'} in your library.
          Nothing is published.
        </Alert>
        {/* Back is only offered here, before confirm — once the worker is
            running there is nothing to go back to. */}
        <div className="flex justify-between">
          <Button onClick={onBack}>Back</Button>
          <Button variant="contained" onClick={onConfirm}>
            Import {selectedCount} creative{selectedCount === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    )
  }

  const counts = progress?.counts
  const finished = counts ? counts.done + counts.error : 0

  return (
    <div className="flex flex-col gap-4">
      {state.confirmError && <Alert severity="error">{state.confirmError}</Alert>}
      <div className="flex items-center gap-3">
        <CircularProgress size={20} />
        <p className="text-mm-neutral-1-900">
          Importing… {counts ? `${finished} of ${counts.selected} finished` : 'starting'}
        </p>
      </div>
      {counts && (
        <LinearProgress
          variant="determinate"
          value={counts.selected ? (finished / counts.selected) * 100 : 0}
        />
      )}
      <div className="divide-y divide-mm-neutral-1-200 rounded-lg border border-mm-neutral-1-200 bg-white">
        {(progress?.rows ?? []).map(row => (
          <div key={row.rowNumber} className="px-4 py-2">
            <div className="flex items-center justify-between gap-3">
              <span className="truncate">{row.effectiveName}</span>
              <Chip size="small" {...ROW_STATUS_CHIP[row.status]} variant="outlined" />
            </div>
            {row.status === CreativeImportRowStatus.Error && row.errorMessage && (
              <p className="text-xs text-mm-warning-600">{row.errorMessage}</p>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-mm-neutral-1-500">
        Imports run on the server — closing this window will not stop them; finished drafts appear
        in your creative library.
      </p>
    </div>
  )
}
